import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProfile } from '../services/userProfileService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './MyProfile.css';

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [isLoggedIn, authLoading, navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await getMyProfile();
      setProfile(data);
      setError('');
    } catch (err) {
      setError('Failed to load your profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getContrastColor = (hexColor) => {
    if (!hexColor) return "#000000";
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'confirmed':
        return 'status-confirmed';
      case 'checked_in':
        return 'status-checked-in';
      case 'cancelled':
        return 'status-cancelled';
      case 'no_show':
        return 'status-no-show';
      default:
        return 'status-pending';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="my-profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="error-message">
            <i className="bi bi-exclamation-circle"></i>
            <p>{error}</p>
            <button onClick={fetchProfile} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="my-profile-page">
      <Navbar />

      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">
            <i className="bi bi-person-circle"></i>
          </div>
          <div className="profile-info">
            <h1>
              {profile?.user?.firstName && profile?.user?.lastName
                ? `${profile.user.firstName} ${profile.user.lastName}`
                : 'My Profile'}
            </h1>
            <p className="profile-email">{profile?.user?.email}</p>
            {profile?.user?.phoneNumber && (
              <p className="profile-phone">
                <i className="bi bi-telephone"></i> {profile.user.phoneNumber}
              </p>
            )}
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="bi bi-calendar-check"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{profile?.stats?.totalReservations || 0}</span>
              <span className="stat-label">Total Reservations</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon completed">
              <i className="bi bi-check-circle"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{profile?.stats?.completedReservations || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon upcoming">
              <i className="bi bi-clock"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{profile?.stats?.upcomingReservations || 0}</span>
              <span className="stat-label">Upcoming</span>
            </div>
          </div>
        </div>

        {/* Tags Section */}
        {profile?.tags && profile.tags.length > 0 && (
          <div className="tags-section">
            <h2><i className="bi bi-tags me-2"></i>Your Tags</h2>
            <p className="section-description">
              These tags have been assigned to your reservations by restaurant staff.
            </p>
            <div className="tags-list">
              {profile.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="tag-badge"
                  style={{
                    backgroundColor: tag.color,
                    color: getContrastColor(tag.color),
                  }}
                >
                  {tag.name}
                  {tag.count > 1 && (
                    <span className="tag-count">x{tag.count}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Reservation History Section */}
        <div className="history-section">
          <h2><i className="bi bi-clock-history me-2"></i>Reservation History</h2>

          {profile?.reservationHistory && profile.reservationHistory.length > 0 ? (
            <div className="reservations-list">
              {profile.reservationHistory.map((reservation) => (
                <div key={reservation.id} className="reservation-card">
                  <div className="reservation-header">
                    <div className="restaurant-info">
                      <i className="bi bi-shop"></i>
                      <h3>{reservation.restaurantName}</h3>
                    </div>
                    <span className={`status-badge ${getStatusBadgeClass(reservation.status)}`}>
                      {reservation.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="reservation-details">
                    <div className="detail-item">
                      <i className="bi bi-calendar3"></i>
                      <span>{formatDate(reservation.reservationDate)}</span>
                    </div>
                    <div className="detail-item">
                      <i className="bi bi-clock"></i>
                      <span>{reservation.reservationTime}</span>
                    </div>
                    <div className="detail-item">
                      <i className="bi bi-people"></i>
                      <span>{reservation.partySize} {reservation.partySize === 1 ? 'Guest' : 'Guests'}</span>
                    </div>
                    {reservation.tableName && (
                      <div className="detail-item">
                        <i className="bi bi-grid-3x3"></i>
                        <span>Table: {reservation.tableName}</span>
                      </div>
                    )}
                  </div>

                  {reservation.specialOccasion && (
                    <div className="special-occasion">
                      <i className="bi bi-star-fill"></i>
                      <span>{reservation.specialOccasion}</span>
                    </div>
                  )}

                  {/* Reservation Tags */}
                  {reservation.tags && reservation.tags.length > 0 && (
                    <div className="reservation-tags">
                      {reservation.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="mini-tag"
                          style={{
                            backgroundColor: tag.color,
                            color: getContrastColor(tag.color),
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Reservation Notes */}
                  {reservation.notes && reservation.notes.length > 0 && (
                    <div className="reservation-notes">
                      <h4><i className="bi bi-journal-text me-1"></i>Notes</h4>
                      <div className="notes-list">
                        {reservation.notes.map((note) => (
                          <div key={note.id} className="note-item">
                            <p>{note.content}</p>
                            <span className="note-meta">
                              {note.createdBy
                                ? `${note.createdBy.firstName || ''} ${note.createdBy.lastName || ''}`.trim()
                                : 'Staff'} - {formatDate(note.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <i className="bi bi-calendar-x"></i>
              <p>No reservation history yet</p>
              <button onClick={() => navigate('/')} className="browse-btn">
                Browse Restaurants
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyProfile;
